/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/class/DateTime.ts
 * @create:      2021-08-05 15:09:01.492
 * @modify:      2025-07-08 14:32:04.990
 * @version:     6.0.0
 * @times:       121
 * @lines:       261
 * @copyright:   Copyright © 2021-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { MillisecondsPerDay, MillisecondsPerHour, MillisecondsPerMinute, MillisecondsPerSecond, MillisecondsPerWeek } from "../const/TimeFactor";
import { GetDayOfWeekTdd } from "../data/GetDayOfWeekTdd";
import { GetWeekOfYear } from "../date/GetWeekOfYear";
import { DateTimeFormat } from "../enum/DateTimeFormat";
import { DayOfWeek } from "../enum/DayOfWeek";
import { GetStringPositionBetween } from "../string/GetStringPositionBetween";
import { ReplaceAll } from "../string/ReplaceAll";
import { TTimeUnit } from "../type/advanced";

export class DateTime {
    private millisecondsSinceEpoch: number;
    private date!: Date;

    public constructor(millisecondsSinceEpoch?: number) {
        this.millisecondsSinceEpoch = millisecondsSinceEpoch ?? Date.now();
        this.update();
    }

    public get Year() {
        return this.date.getUTCFullYear();
    }

    /**
     * 1 - 12
     */
    public get Month() {
        return this.date.getUTCMonth() + 1;
    }

    /**
     * 1 - 31
     */
    public get Day() {
        return this.date.getUTCDate();
    }

    /**
     * 0 for Sunday, 1 for Monday, 2 for Tuesday, and so on.
     */
    public get DayOfWeek(): DayOfWeek {
        return this.date.getUTCDay();
    }

    /**
     * ISO
     */
    public get WeekOfYear() {
        return GetWeekOfYear(this.date, true);
    }

    /**
     * 0 - 23
     */
    public get Hours() {
        return this.date.getUTCHours();
    }

    /**
     * 0 - 59
     */
    public get Minutes() {
        return this.date.getUTCMinutes();
    }

    /**
     * 0 - 59
     */
    public get Seconds() {
        return this.date.getUTCSeconds();
    }

    /**
     * 0 - 999
     */
    public get Milliseconds() {
        return this.date.getUTCMilliseconds();
    }

    private update() {
        this.date = new Date(this.millisecondsSinceEpoch);
    }

    private formatNumber(number: number, length = 2) {
        return number.toString().padStart(length, "0");
    }

    private format(str: string) {
        let result = str;

        result = ReplaceAll(result, "YYYY", this.formatNumber(this.Year, 4));
        result = ReplaceAll(result, "MM", this.formatNumber(this.Month));
        result = ReplaceAll(result, "DD", this.formatNumber(this.Day));
        result = ReplaceAll(result, "HH", this.formatNumber(this.Hours));
        result = ReplaceAll(result, "mm", this.formatNumber(this.Minutes));
        result = ReplaceAll(result, "ss", this.formatNumber(this.Seconds));
        result = ReplaceAll(result, "SSS", this.formatNumber(this.Milliseconds, 3));

        const DayOfWeekTdd = GetDayOfWeekTdd();
        result = ReplaceAll(result, "dd", DayOfWeekTdd[this.DayOfWeek]);
        result = ReplaceAll(result, "ww", this.formatNumber(GetWeekOfYear(this.date, false)));
        result = ReplaceAll(result, "WW", this.formatNumber(GetWeekOfYear(this.date, true)));

        return result;
    }

    public Add(value: number): this;
    public Add(value: number, unit: TTimeUnit): this;
    public Add(value: number, unit: TTimeUnit = "milliseconds") {
        const add = (ms: number) => {
            this.millisecondsSinceEpoch += ms;
            this.update();
            return this;
        };

        switch (unit) {
            case "milliseconds":
                return add(value);
            case "seconds":
                return add(value * MillisecondsPerSecond);
            case "minutes":
                return add(value * MillisecondsPerMinute);
            case "hours":
                return add(value * MillisecondsPerHour);
            case "days":
                return add(value * MillisecondsPerDay);
            case "weeks":
                return add(value * MillisecondsPerWeek);

            default:
                throw new TypeError(`Not support: ${unit}`);
        }
    }

    public Subtract(value: number): this;
    public Subtract(value: number, unit: TTimeUnit): this;
    public Subtract(value: number, unit: TTimeUnit = "milliseconds") {
        return this.Add(-value, unit);
    }

    public Diff(another: DateTime): number;
    public Diff(another: DateTime, unit: TTimeUnit): number;
    public Diff(another: DateTime, unit?: TTimeUnit): number {
        const diffMs = this.millisecondsSinceEpoch - another.millisecondsSinceEpoch;
        let diff: number | undefined;
        switch (unit) {
            case undefined:
            case "milliseconds":
                diff = diffMs;
                break;
            case "seconds":
                diff = diffMs / MillisecondsPerSecond;
                break;
            case "minutes":
                diff = diffMs / MillisecondsPerMinute;
                break;
            case "hours":
                diff = diffMs / MillisecondsPerHour;
                break;
            case "days":
                diff = diffMs / MillisecondsPerDay;
                break;
            case "weeks":
                diff = diffMs / MillisecondsPerWeek;
                break;
            default:
                throw TypeError(`Not support: ${unit}`);
        }

        return diff;
    }

    public Clone() {
        return new DateTime(this.millisecondsSinceEpoch);
    }

    public ToString(): string;
    public ToString(format?: DateTimeFormat | string, timezoneOffset?: number): string;
    public ToString(format: DateTimeFormat | string = DateTimeFormat.FullDataTime, timezoneOffset = 0): string {
        if (timezoneOffset !== 0) {
            const dt = this.Clone();
            dt.Subtract(timezoneOffset, "minutes");
            return dt.ToString(format, 0);
        }

        let str = format.toString();

        const list = GetStringPositionBetween(str, "[", "]");
        if (list.length > 0) {
            let result = "";
            let i = 0;
            for (const sp of list) {
                result += this.format(str.substring(i, sp.Start - 1));
                result += str.substring(sp.Start, sp.End!);
                i = sp.End! + 1;
            }
            str = result + this.format(str.substring(i));
        } else {
            str = this.format(str);
        }

        return str;
    }

    public toString() {
        return this.ToString();
    }

    public ToLocalString(): string;
    public ToLocalString(format?: DateTimeFormat | string): string;
    public ToLocalString(format: DateTimeFormat | string = DateTimeFormat.FullDataTime): string {
        return this.ToString(format, this.date.getTimezoneOffset());
    }

    public ToDate() {
        // return new Date(Date.UTC(this.Year, this.Month - 1, this.Day, this.Hours, this.Minutes, this.Seconds, this.Milliseconds));
        return new Date(this.millisecondsSinceEpoch);
    }

    public Equals(another: DateTime) {
        return this.millisecondsSinceEpoch === another.millisecondsSinceEpoch;
    }

    public static From(date: Date): DateTime;
    public static From(date: Date, timezoneOffset: number): DateTime;
    public static From(date: Date, timezoneOffset = 0) {
        return new DateTime(date.valueOf() + timezoneOffset * MillisecondsPerMinute);
    }
}
